import { useSyncExternalStore } from 'react';
import { useAuth } from '../../context/AuthContext';

/**
 * Which store group the hotel dashboard opens. A user in a group always gets
 * their own. A platform admin picks one; the pick lasts across reloads like
 * the selected store does. Nobody else has a group to open, so the dashboard
 * stays Not found for them.
 *
 * Stored as `<user id>:<group id>`: sign-out doesn't clear it, so a pick only
 * counts for the admin who made it, never the next one to sign in.
 *
 * (Ported from #295. The cross-shell "is there a hotel" signal that used to
 * live here as `useHotelEntryVisible` is `useExperiences` in
 * `shell/experiences.ts`, IA §3.)
 */
export const HOTEL_GROUP_KEY = 'av2.hotel.group';

const listeners = new Set<() => void>();
/** Holds the pick when storage is unavailable (Safari private mode). */
let fallback: string | null = null;

function readStored(): string | null {
  try {
    return localStorage.getItem(HOTEL_GROUP_KEY);
  } catch {
    return fallback;
  }
}

/** `userId`'s pick: a group, `null` once cleared, `undefined` if none. */
function parsePick(
  value: string | null,
  userId: number | undefined,
): number | null | undefined {
  const [owner, group] = value?.split(':') ?? [];
  if (userId === undefined || owner !== String(userId)) return undefined;
  if (group === '') return null;
  const id = Number(group);
  return Number.isInteger(id) && id > 0 ? id : undefined;
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  window.addEventListener('storage', listener);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', listener);
  };
}

/**
 * Platform admin `userId`'s choice of group; `null` goes back to the picker.
 * A cleared pick is stored (`<user id>:`) so the selected store's group,
 * which only applies before any pick, doesn't take its place.
 */
export function pickHotelGroup(userId: number, id: number | null) {
  const value = `${userId}:${id ?? ''}`;
  fallback = value;
  try {
    localStorage.setItem(HOTEL_GROUP_KEY, value);
  } catch {
    /* the pick lasts for this page load */
  }
  listeners.forEach((listener) => listener());
}

export type HotelOrganization = {
  /** The group whose hotels are shown; null while there is none to show. */
  organizationId: number | null;
  /** A platform admin, who chooses the group. */
  picksGroup: boolean;
  /** user-info hasn't yet said whether the user is in a group. */
  pending: boolean;
};

export function useHotelOrganization(): HotelOrganization {
  const { user, userInfoSettled } = useAuth();
  const picked = parsePick(
    useSyncExternalStore(subscribe, readStored),
    user?.id,
  );
  const own = user?.store_group_id ?? null;
  if (user?.is_platform_admin === true) {
    // user-info fills a platform admin's group from the store they have
    // selected, not from a membership: it's where to start, not a limit.
    return {
      organizationId: picked === undefined ? own : picked,
      picksGroup: true,
      pending: false,
    };
  }
  if (own !== null) {
    return { organizationId: own, picksGroup: false, pending: false };
  }
  if (!userInfoSettled) {
    return { organizationId: null, picksGroup: false, pending: true };
  }
  return { organizationId: null, picksGroup: false, pending: false };
}
